/**
 * DataSectionHeader — New folder + Refresh toolbar (optional label).
 * variant="icons" + embedded: icon-only buttons for the Run tab Data widget header row.
 */
import React, { useState } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/** Folder outline only (new folder action — no + overlay to avoid cramped layout) */
function FolderIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12.75V12a2.25 2.25 0 012.25-2.25h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
      />
    </svg>
  );
}

function RefreshIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

const iconBtnClass =
  'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-200/90 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50';

export default function DataSectionHeader({
  label,
  hideLabel = false,
  variant = 'default',
  embedded = false,
  type,
  networkId,
  currentPrefix,
  fileListRef,
  onRefresh,
  hideNewFolder = false
}) {
  const { user } = useAuth();
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [createFolderError, setCreateFolderError] = useState(null);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    setCreateFolderError(null);
    try {
      const idToken = getRestApiBearerToken();
      const res = await fetch(`${API_BASE_URL}/networks/${networkId}/files/folders`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, prefix: currentPrefix, folderName: name })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCreateFolderError(data.error || res.statusText);
        return;
      }
      setShowCreateFolder(false);
      setNewFolderName('');
      fileListRef.current?.refresh?.();
      fileListRef.current?.navigateToPrefix?.(
        data.currentPrefix || (currentPrefix ? `${currentPrefix}${name}/` : `${name}/`)
      );
    } catch (e) {
      setCreateFolderError(e.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const cancelCreateFolder = () => {
    setShowCreateFolder(false);
    setNewFolderName('');
    setCreateFolderError(null);
  };

  const createFolderForm = (
    <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2">
      <input
        type="text"
        value={newFolderName}
        onChange={(e) => setNewFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleCreateFolder();
          if (e.key === 'Escape') cancelCreateFolder();
        }}
        placeholder="Folder name"
        className="min-w-0 flex-1 max-w-[220px] px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        autoFocus
        disabled={creatingFolder}
      />
      <button
        type="button"
        onClick={handleCreateFolder}
        disabled={creatingFolder || !newFolderName.trim()}
        className="px-2.5 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
      >
        {creatingFolder ? '...' : 'Create'}
      </button>
      <button
        type="button"
        onClick={cancelCreateFolder}
        disabled={creatingFolder}
        className="px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-200 rounded-md flex-shrink-0"
      >
        Cancel
      </button>
    </div>
  );

  if (variant === 'icons' && embedded) {
    return (
      <div
        className={`flex min-w-0 flex-col items-end gap-1.5 ${
          showCreateFolder ? 'w-full basis-full sm:basis-auto' : 'flex-shrink-0'
        }`}
      >
        {showCreateFolder ? (
          createFolderForm
        ) : (
          <div className="flex items-center gap-0.5">
            {!hideNewFolder && (
              <button
                type="button"
                onClick={() => {
                  setShowCreateFolder(true);
                  setCreateFolderError(null);
                }}
                className={iconBtnClass}
                title="New folder"
                aria-label="New folder"
              >
                <FolderIcon className="h-5 w-5 text-amber-800/90" />
              </button>
            )}
            <button
              type="button"
              onClick={onRefresh}
              className={iconBtnClass}
              title="Refresh"
              aria-label="Refresh file list"
            >
              <RefreshIcon />
            </button>
          </div>
        )}
        {createFolderError ? (
          <span className="max-w-full text-right text-xs text-red-600">{createFolderError}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-100 flex items-center gap-2 min-h-0">
      {!hideLabel && label != null && label !== '' && (
        <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{label}</span>
      )}
      <div className="flex-1 flex items-center gap-2 min-w-0">
        {showCreateFolder ? (
          <>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') cancelCreateFolder();
              }}
              placeholder="Folder name"
              className="flex-1 min-w-0 max-w-[140px] px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
              disabled={creatingFolder}
            />
            <button
              type="button"
              onClick={handleCreateFolder}
              disabled={creatingFolder || !newFolderName.trim()}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
            >
              {creatingFolder ? '...' : 'Create'}
            </button>
            <button
              type="button"
              onClick={cancelCreateFolder}
              disabled={creatingFolder}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded flex-shrink-0"
            >
              Cancel
            </button>
          </>
        ) : (
          !hideNewFolder && (
            <button
              type="button"
              onClick={() => {
                setShowCreateFolder(true);
                setCreateFolderError(null);
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded transition-colors"
              title="New folder"
            >
              <FolderIcon className="h-4 w-4" />
              New folder
            </button>
          )
        )}
      </div>
      {createFolderError && (
        <span className="text-xs text-red-600 truncate flex-shrink min-w-0">{createFolderError}</span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors flex-shrink-0 ml-auto"
        title="Refresh"
        aria-label="Refresh"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      </button>
    </div>
  );
}
